import { ReactNode } from "react";

export type DonutChartDataItem = {
  /** The value for this segment */
  value: number;
  /** Color identifier (used by styled component) */
  color: string;
  /** Optional label for the segment */
  label?: string;
};

export type Props = {
  /** Data items for the chart segments */
  data: DonutChartDataItem[];
  /** Size of the chart in pixels */
  size?: number;
  /** Thickness of the donut ring */
  thickness?: number;
  /** Additional CSS classes */
  className?: string;
  /** Children to render in the center */
  children?: ReactNode;
  /** Whether to show the center label */
  showCenterLabel?: boolean;
  /** Whether to show the legend */
  showLegend?: boolean;
  /** Position of the legend */
  legendPosition?: "top" | "right" | "bottom" | "left";
  /** Whether to show percentage in legend */
  showPercentage?: boolean;
  /** Whether this is a percentage meter (affects full circle rendering) */
  isPercentageMeter?: boolean;
  /** Test ID for testing */
  "data-testid"?: string;
  /** Render prop for the SVG segments */
  renderSegment?: (props: {
    path: string;
    color: string;
    index: number;
  }) => ReactNode;
  /** Render prop for the legend item */
  renderLegendItem?: (props: {
    item: DonutChartDataItem;
    index: number;
    percentage: number;
  }) => ReactNode;
  /** Render prop for the center content */
  renderCenter?: (props: { children: ReactNode }) => ReactNode;
  /** Render prop for empty state circle */
  renderEmptyCircle?: (props: {
    cx: number;
    cy: number;
    r: number;
    strokeWidth: number;
  }) => ReactNode;
  /** Render prop for full circle (single segment) */
  renderFullCircle?: (props: {
    cx: number;
    cy: number;
    r: number;
    strokeWidth: number;
    color: string;
  }) => ReactNode;
  /** Render prop for background circle */
  renderBackgroundCircle?: (props: {
    cx: number;
    cy: number;
    r: number;
    strokeWidth: number;
  }) => ReactNode;
};

/**
 * Calculates the SVG path for a donut segment
 */
function calculatePath(
  value: number,
  total: number,
  currentAngle: number,
  center: number,
  radius: number,
  dataLength: number
): { path: string; endAngle: number } {
  const percentage = value / total;
  const startAngle = currentAngle;
  const endAngle = startAngle + percentage * 360;

  const startRadians = (startAngle - 90) * (Math.PI / 180);
  const endRadians = (endAngle - 90) * (Math.PI / 180);

  const startX = center + radius * Math.cos(startRadians);
  const startY = center + radius * Math.sin(startRadians);
  const endX = center + radius * Math.cos(endRadians);
  const endY = center + radius * Math.sin(endRadians);

  // For a full circle (single segment with value 100)
  if (dataLength === 1 && value === 100) {
    return {
      path: `M ${startX} ${startY} A ${radius} ${radius} 0 1 1 ${startX} ${startY}`,
      endAngle,
    };
  }

  const largeArcFlag = percentage > 0.5 ? 1 : 0;
  return {
    path: `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
    endAngle,
  };
}

/**
 * Base donut chart component with no styling.
 * Handles SVG path calculations and data processing.
 * Uses render props for customizable segments, legend, and center content.
 */
export function DonutChartBase({
  data,
  size = 200,
  thickness = 20,
  className,
  children,
  showCenterLabel = false,
  showLegend = false,
  legendPosition = "bottom",
  showPercentage = false,
  isPercentageMeter = false,
  "data-testid": testId = "donut-chart-base",
  renderSegment,
  renderLegendItem,
  renderCenter,
  renderEmptyCircle,
  renderFullCircle,
  renderBackgroundCircle,
}: Props) {
  const total =
    data.length === 1 ? 100 : data.reduce((sum, item) => sum + item.value, 0);
  const radius = (size - thickness) / 2;
  const center = size / 2;

  const nonZeroValues = data.filter((item) => item.value > 0);

  const shouldRenderFullCircle =
    nonZeroValues.length === 1 &&
    nonZeroValues[0].value === data.reduce((sum, item) => sum + item.value, 0) &&
    !isPercentageMeter;

  const shouldRenderEmptyCircle = nonZeroValues.length === 0;

  // Filter out zero-value items for rendering chart only
  const filteredData = data.filter((item) => item.value > 0);

  // Calculate paths for all segments
  let currentAngle = 0;
  const segments = filteredData.map((item) => {
    const { path, endAngle } = calculatePath(
      item.value,
      total,
      currentAngle,
      center,
      radius,
      data.length
    );
    currentAngle = endAngle;
    return { path, color: item.color };
  });

  return (
    <div className={className} data-testid={testId}>
      <div className="relative" data-testid={`${testId}-chart`}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {shouldRenderEmptyCircle &&
            (renderEmptyCircle ? (
              renderEmptyCircle({
                cx: center,
                cy: center,
                r: radius,
                strokeWidth: thickness,
              })
            ) : (
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                strokeWidth={thickness}
                data-testid={`${testId}-empty-circle`}
              />
            ))}

          {shouldRenderFullCircle &&
            (renderFullCircle ? (
              renderFullCircle({
                cx: center,
                cy: center,
                r: radius,
                strokeWidth: thickness,
                color: nonZeroValues[0].color,
              })
            ) : (
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                strokeWidth={thickness}
                data-testid={`${testId}-full-circle`}
                data-color={nonZeroValues[0].color}
              />
            ))}

          {!shouldRenderEmptyCircle && !shouldRenderFullCircle && filteredData.length > 0 && (
            <>
              {renderBackgroundCircle ? (
                renderBackgroundCircle({
                  cx: center,
                  cy: center,
                  r: radius,
                  strokeWidth: thickness,
                })
              ) : (
                <circle
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  strokeWidth={thickness}
                  data-testid={`${testId}-background-circle`}
                />
              )}
              {segments.map((segment, index) =>
                renderSegment ? (
                  renderSegment({
                    path: segment.path,
                    color: segment.color,
                    index,
                  })
                ) : (
                  <path
                    key={index}
                    d={segment.path}
                    fill="none"
                    strokeWidth={thickness}
                    strokeLinecap="round"
                    data-testid={`${testId}-segment-${index}`}
                    data-color={segment.color}
                  />
                )
              )}
            </>
          )}
        </svg>

        {showCenterLabel &&
          children &&
          (renderCenter ? (
            renderCenter({ children })
          ) : (
            <div data-testid={`${testId}-center`}>{children}</div>
          ))}
      </div>

      {showLegend && (
        <div data-testid={`${testId}-legend`} data-position={legendPosition}>
          {data.map((item, index) =>
            renderLegendItem ? (
              renderLegendItem({
                item,
                index,
                percentage: Math.round((item.value / total) * 100),
              })
            ) : (
              <div key={index} data-testid={`${testId}-legend-item-${index}`}>
                <span data-color={item.color} />
                {item.label && (
                  <span>
                    {showPercentage && `${Math.round((item.value / total) * 100)}% `}
                    {item.label}
                  </span>
                )}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

DonutChartBase.displayName = "DonutChartBase";

